/**
 * Context menus.
 *
 * The browser's own menu is suppressed app-wide (a CAD canvas has nothing useful in "Save image
 * as…"), so the app provides its own: a small floating list anchored at the pointer, closed by
 * click-away, Escape, scroll, or picking an item.
 */

export interface ContextMenuItem {
  label: string;
  hint?: string;
  /** Renders the item in the danger colour and asks for confirmation at the call site. */
  danger?: boolean;
  disabled?: boolean;
  run(): void;
}

let closeCurrent: (() => void) | null = null;

export function closeContextMenu(): void {
  closeCurrent?.();
}

/** Shows a menu at viewport coordinates. Returns a function that closes it. */
export function showContextMenu(
  x: number,
  y: number,
  items: ContextMenuItem[],
  title?: string,
): () => void {
  closeCurrent?.();

  const menu = document.createElement('div');
  menu.className = 'ctx-menu';
  menu.setAttribute('role', 'menu');

  if (title) {
    const head = document.createElement('div');
    head.className = 'ctx-title';
    head.textContent = title;
    menu.append(head);
  }

  for (const item of items) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = item.danger ? 'ctx-item is-danger' : 'ctx-item';
    button.setAttribute('role', 'menuitem');
    button.disabled = item.disabled === true;

    const label = document.createElement('span');
    label.textContent = item.label;
    button.append(label);
    if (item.hint) {
      const hint = document.createElement('span');
      hint.className = 'ctx-hint';
      hint.textContent = item.hint;
      button.append(hint);
    }

    button.addEventListener('click', (event) => {
      event.stopPropagation();
      close();
      item.run();
    });
    menu.append(button);
  }

  document.body.append(menu);

  // Keep the menu inside the window.
  const box = menu.getBoundingClientRect();
  const left = Math.min(x, window.innerWidth - box.width - 6);
  const top = Math.min(y, window.innerHeight - box.height - 6);
  menu.style.left = `${Math.max(6, left)}px`;
  menu.style.top = `${Math.max(6, top)}px`;

  const onPointerDown = (event: PointerEvent): void => {
    if (!menu.contains(event.target as Node)) close();
  };
  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
  };
  const close = (): void => {
    menu.remove();
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('keydown', onKey, true);
    window.removeEventListener('blur', close);
    window.removeEventListener('resize', close);
    if (closeCurrent === close) closeCurrent = null;
  };

  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('keydown', onKey, true);
  window.addEventListener('blur', close);
  window.addEventListener('resize', close);
  closeCurrent = close;

  return close;
}

/**
 * Stops the browser menu everywhere except in editable fields, where paste/undo still belong to
 * the browser. Call once at boot.
 */
export function suppressBrowserContextMenu(): void {
  document.addEventListener(
    'contextmenu',
    (event) => {
      const target = event.target as HTMLElement | null;
      const editable =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable === true;
      if (editable) return;
      event.preventDefault();
    },
    { capture: true },
  );
}
